<template>
    <div
        :class="[$style['mds-inline-message'], classObject, sizeClass]"
        :data-mds-version="version"
        :aria-label="iconAriaLabel"
    >
        <mds-icon :name="iconName" :class="$style['mds-inline-message__icon']" :size="iconSize" />
        <div :class="$style['mds-inline-message__body']" v-if="!iconOnly">
            <slot> {{ text }} </slot>
        </div>
    </div>
</template>

<script>
    import packageInfo from '../package.json';
    import MdsIcon from '@mds/icon';
    const { name, version } = packageInfo;

    export default {
        name: 'MdsInlineMessage',
        components: {
            MdsIcon,
        },
        data() {
            return {
                version: `${name}-${version}`,
            };
        },
        props: {
            onDark: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, changes component to on-dark styling.',
                },
            },
            size: {
                type: String,
                default: 'medium',
                validator(value) {
                    return ['small', 'medium', 'large', 'touch'].indexOf(value) !== -1;
                },
                docs: {
                    validation: 'One of: `small`, `medium`, `large`, `touch`.',
                    description: 'Sets the size of the inline message.',
                },
            },
            variation: {
                type: String,
                default: 'informational',
                validator(value) {
                    return ['error', 'warning', 'informational', 'success'].indexOf(value) !== -1;
                },
                docs: {
                    validation: 'One of: `error`, `warning`, `success`, `informational`.',
                    description: 'Sets the variation of the inline message.',
                },
            },
            tinted: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '`variation` must be one of: `error`, `warning`, `success`.',
                    description: 'If `true`, adds the tinted background color to the body of the inline message.',
                },
            },
            text: {
                type: String,
                docs: {
                    validation: '**Required**',
                    description:
                        'The text that appears in the inline message. Can also be passed via the default slot.',
                },
            },
            iconOnly: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, does not show the text provided.',
                },
            },
        },
        computed: {
            classObject() {
                const obj = {};
                obj[this.$style['mds-component--on-dark']] = this.onDark;
                obj[this.$style['mds-inline-message--tinted']] = this.tinted;
                obj[this.$style[`mds-inline-message--${this.variation}`]] = true;
                return obj;
            },
            sizeClass() {
                return this.$style[`mds-inline-message--${this.size}`];
            },
            iconSize() {
                const smallIconList = ['small', 'medium'];
                return smallIconList.indexOf(this.size) !== -1 ? 'small' : 'medium';
            },
            iconName() {
                return `status-${this.variation}`;
            },
            iconAriaLabel() {
                let message = this.text;
                if (this.$scopedSlots.default) {
                    message = this.$scopedSlots.default()[0].text;
                }
                return message;
            },
        },
    };
</script>

<style lang="scss" module>
    @import './inline-message.scss';
</style>
